import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, Loader2 } from 'lucide-react';
import { familyApi, errMsg } from './familyApi';
import { AccessCalendar } from './AccessCalendar';
import { AccessTypePicker } from './AccessTypePicker';

const EMPTY = { name: '', relationship: '', email: '', password: '', notes: '', mode: 'custom', dates: [] };
export const inputCls = 'w-full rounded-xl border-2 border-amber-300 bg-white px-3 py-2.5 text-base focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200';

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-amber-900 mb-1">{label}</span>
    {children}
  </label>
);

export const PasswordInput = ({ value, onChange, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testId} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="At least 8 characters" className={`${inputCls} pr-11`} autoComplete="new-password" />
      <button type="button" data-testid={`${testId}-toggle`} onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-800">
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

export const CreateAccountForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (f.mode === 'custom' && f.dates.length === 0) { toast.error('Pick at least one access day, or choose lifetime access'); return; }
    setBusy(true);
    try {
      const acc = await familyApi.create(f);
      toast.success(`Account created for ${acc.name}`);
      setF(EMPTY);
      onCreated(acc);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <form data-testid="family-create-form" onSubmit={submit} className="rounded-3xl border-2 border-amber-300 bg-gradient-to-b from-amber-50 to-yellow-50 p-4 sm:p-6 space-y-4">
      <h3 className="text-xl font-bold text-amber-900">Create an account</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name"><input data-testid="family-create-name" required value={f.name} onChange={(e) => set('name')(e.target.value)} placeholder="e.g., John Smith" className={inputCls} /></Field>
        <Field label="Relationship"><input data-testid="family-create-relationship" value={f.relationship} onChange={(e) => set('relationship')(e.target.value)} placeholder="e.g., Dad, Mum, Friend" className={inputCls} /></Field>
        <Field label="Email (their login)"><input data-testid="family-create-email" required type="email" value={f.email} onChange={(e) => set('email')(e.target.value)} placeholder="e.g., dad@email.com" className={inputCls} autoComplete="off" /></Field>
        <Field label="Password (you set it)"><PasswordInput testId="family-create-password" value={f.password} onChange={set('password')} /></Field>
      </div>
      <Field label="Access"><AccessTypePicker value={f.mode} onChange={set('mode')} testPrefix="family-create-mode" /></Field>
      {f.mode === 'custom' && <AccessCalendar dates={f.dates} onChange={set('dates')} />}
      <Field label="Private notes (only you see these)">
        <textarea data-testid="family-create-notes" rows={2} value={f.notes} onChange={(e) => set('notes')(e.target.value)} placeholder="e.g., Birthday 14 March" className={inputCls} />
      </Field>
      <button type="submit" data-testid="family-create-submit" disabled={busy}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 py-3 text-white font-bold text-base shadow-md hover:from-amber-600 hover:to-orange-600 active:scale-[0.99] transition-transform disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={18} />} Save account
      </button>
    </form>
  );
};
